import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HullCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Hull Pothole Claims | Bus Routes RED Up to 4.1% | Fixtyer"
        description="Hull's strategic U1 bus routes saw RED-condition roads more than double to 4.1% in 2024, while 83% of the 731km network is unclassified. See what the DfT AMBER rating and council transparency data mean for your pothole claim."
        canonical="/councils/hull"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hull', url: '/councils/hull' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Hull has a GREEN condition scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Condition scorecard is GREEN because SCANNER-surveyed A and B/C roads have kept RED sections at a steady 1–2% over five years. That does not cover the whole story: strategic U1 unclassified roads jumped from 1.6% RED in 2023 to 4.1% in 2024, and residential U2 roads rose to 5.4% RED. Section 58 turns on whether your specific defect was reasonably inspected and repaired — not on a headline scorecard.',
          },
          {
            question: 'What if my pothole was on a residential street in Hull?',
            answer: 'Residential U2 roads make up the bulk of Hull\'s 607km unclassified network. They are surveyed by detailed visual inspection (DVI) — a walked survey by a trained surveyor — not SCANNER laser surveys. RED condition on U2 roads reached 5.4% in 2024, up from 4.13% in 2023. Your claim should focus on inspection frequency, prior reports, and whether the walked survey would have caught a defect of your pothole\'s size and age.',
          },
          {
            question: 'Does the U1 bus-route deterioration help my claim?',
            answer: 'Potentially, if your incident was on a U1 strategic road or major bus route. Hull extended SCANNER surveys to 100km of U1 roads in 2020, and RED condition on those routes more than doubled from 1.6% to 4.1% between 2023 and 2024 — while A and B/C roads stayed broadly stable. That is documented deterioration on high-traffic corridors the council itself classifies as comparable to principal roads.',
          },
          {
            question: 'Hull spends above its DfT allocation — does that block Section 58 arguments?',
            answer: 'Not automatically. Hull\'s projected 2025/26 capital spend of £5.86m exceeds its £4.482m DfT allocation, earning a GREEN Spend scorecard. But estimated preventative maintenance is only 9–11% of spend in recent years, and the council still repaired 29,844 potholes and defects across five years. High reactive volume suggests defects routinely form between inspections — the scenario where prior reports and photographic evidence matter most.',
          },
          {
            question: 'Hull uses concrete road bases — does that matter for claims?',
            answer: 'The council states its network consists mainly of concrete base constructions, which are more vulnerable to temperature changes from climate change. It specifies premium SAMI and PMB surfacing materials partly for that reason. Where a pothole forms on a concrete-base road, the council\'s own engineering assessment acknowledges elevated deterioration risk — relevant when arguing whether inspection intervals were reasonable.',
          },
          {
            question: 'How quickly does Hull inspect reported potholes?',
            answer: 'Hull\'s report-a-defect page states inspections within five working days, with repairs scheduled within up to 28 days if considered necessary. The transparency report notes nine highway inspectors undertaking risk-based safety inspections across the network at various intervals annually. If you reported a defect before your incident and it was not repaired in time, that dated record strengthens a Section 41 argument.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Hull City Council"
        councilSlug="hull"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/hull"
        headline="Hull Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hull</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Hull: strategic bus routes in RED condition more than doubled in 2024
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Kingston upon Hull City Council earns GREEN scorecards for condition and spend, yet its overall DfT
            rating is <strong className="text-amber-400">AMBER</strong>. The striking figure is on strategic
            U1 roads — major bus routes and city links where RED condition jumped from{' '}
            <strong className="text-rose-400">1.6% to 4.1% in a single year</strong>, while 83% of Hull's
            731km road network is unclassified and most residential streets rely on walked visual surveys, not
            SCANNER data.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">4.1%</div>
            <div className="text-white font-medium mt-2">U1 strategic roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 1.6% in 2023 on 100km of bus routes and city links that Hull itself SCANNER-surveys
              as comparable to principal A roads — while A-road RED held steady at 2.2%.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Hull's own transparency report — stable classified roads,
            deteriorating strategic unclassified routes
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Principal A roads (52km — 7.1% of network): broadly stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2.2%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">range 1.4–2.6% since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">24.2%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 20.9% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">73.7%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 77.6% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED sections have stayed in a narrow band. Amber has edged up — roughly a quarter of
              principal roads may need maintenance soon.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">Non-principal B and C roads (72km — 9.8% of network): steady state</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">0.8%</td>
                    <td className="px-4 py-2 text-center">14.7%</td>
                    <td className="px-4 py-2 text-center">84.5%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.1%</td>
                    <td className="px-4 py-2 text-center">19.4%</td>
                    <td className="px-4 py-2 text-center">78.5%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.8%</td>
                    <td className="px-4 py-2 text-center">17.7%</td>
                    <td className="px-4 py-2 text-center">80.5%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.14%</td>
                    <td className="px-4 py-2 text-center">16.0%</td>
                    <td className="px-4 py-2 text-center">82.9%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">1.9%</td>
                    <td className="px-4 py-2 text-center font-bold">18.0%</td>
                    <td className="px-4 py-2 text-center font-bold">80.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 text-sm">
              The council credits surface dressing on amber sections for keeping RED at a steady state on B and
              C roads — supporting the GREEN condition scorecard on classified routes.
            </p>
          </div>

          {/* U1 deterioration */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-semibold text-amber-900 mb-4">Strategic U1 roads (100km SCANNER-surveyed): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.0%</td>
                    <td className="px-4 py-2 text-center">13.5%</td>
                    <td className="px-4 py-2 text-center">85.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">19.3%</td>
                    <td className="px-4 py-2 text-center">78.1%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.4%</td>
                    <td className="px-4 py-2 text-center">15.6%</td>
                    <td className="px-4 py-2 text-center">83.0%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">16.3%</td>
                    <td className="px-4 py-2 text-center">82.1%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">23.0%</td>
                    <td className="px-4 py-2 text-center font-bold">72.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED on U1 strategic roads more than doubled in 2024, and amber jumped to 23%. Green fell below
              73% — a sharper deterioration than on any classified road category.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road split */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 607km unclassified split
          </h2>
          <p className="text-surface-600 mb-8">
            83% of Hull's road network is unclassified — surveyed two different ways
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">U1 roads — SCANNER (100km)</h3>
              <p className="text-surface-600 text-sm mb-3">
                In 2020, Hull split its unclassified network into U1 strategic roads (major bus routes and city
                links) and U2 residential roads. The 100km of U1 roads receive the same laser-based SCANNER
                surveys as A and B/C roads.
              </p>
              <p className="text-surface-600 text-sm">
                These are the routes where 4.1% RED in 2024 tells a deterioration story the classified-road
                averages do not.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">U2 roads — walked DVI (~507km)</h3>
              <p className="text-amber-800 text-sm mb-3">
                The remaining residential U2 roads are surveyed by detailed visual inspection — a walked survey
                undertaken by a trained surveyor. This is less precise than SCANNER but cheaper to deploy across
                estate streets.
              </p>
              <p className="text-amber-800 text-sm">
                RED on U2 roads reached <strong>5.4% in 2024</strong>, up from 4.13% in 2023 — the highest
                RED share of any road category Hull publishes.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">U2 residential roads — year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Red</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">Amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center">4.18%</td>
                  <td className="px-6 py-4 text-center">11.09%</td>
                  <td className="px-6 py-4 text-center">84.73%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">4.04%</td>
                  <td className="px-6 py-4 text-center">10.94%</td>
                  <td className="px-6 py-4 text-center">85.02%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center">3.75%</td>
                  <td className="px-6 py-4 text-center">11.67%</td>
                  <td className="px-6 py-4 text-center">84.72%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">4.13%</td>
                  <td className="px-6 py-4 text-center">11.15%</td>
                  <td className="px-6 py-4 text-center">84.72%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5.4%</td>
                  <td className="px-6 py-4 text-center font-bold">11.2%</td>
                  <td className="px-6 py-4 text-center font-bold">83.4%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Hull's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road U1 (SCANNER) or U2 (walked DVI) — and does the survey method match the traffic level?</li>
              <li>• If U2 RED hit 5.4% in 2024, what was done about defects on your street between surveys?</li>
              <li>• Nine highway inspectors cover 731km of roads plus 1,083km of footpaths — was your road inspected before your incident?</li>
              <li>• From 2026/27, PAS 2161 replaces the three-category system — will pre-change records still be comparable?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim the same detailed condition knowledge on walked residential surveys that
              it has on SCANNER-measured strategic routes — yet most of Hull's network is U2.
            </p>
          </div>
        </div>
      </section>

      {/* Spend */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN spend, low preventative share
          </h2>
          <p className="text-surface-600 mb-8">
            Hull exceeds its DfT allocation — but preventative maintenance remains a single-digit percentage
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Financial year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£3,856,000</td>
                  <td className="px-6 py-4 text-center">£6,090,803</td>
                  <td className="px-6 py-4 text-center">£1,640,345</td>
                  <td className="px-6 py-4 text-center">9%</td>
                  <td className="px-6 py-4 text-center">27%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£2,810,000</td>
                  <td className="px-6 py-4 text-center">£6,303,836</td>
                  <td className="px-6 py-4 text-center">£700,877</td>
                  <td className="px-6 py-4 text-center">9%</td>
                  <td className="px-6 py-4 text-center">33%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£2,810,000</td>
                  <td className="px-6 py-4 text-center">£6,766,782</td>
                  <td className="px-6 py-4 text-center">£1,027,755</td>
                  <td className="px-6 py-4 text-center">5%</td>
                  <td className="px-6 py-4 text-center">37%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£2,810,000</td>
                  <td className="px-6 py-4 text-center">£4,000,486</td>
                  <td className="px-6 py-4 text-center">£1,816,083</td>
                  <td className="px-6 py-4 text-center">10%</td>
                  <td className="px-6 py-4 text-center">31%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£2,810,000</td>
                  <td className="px-6 py-4 text-center">£4,648,632</td>
                  <td className="px-6 py-4 text-center">£1,619,935</td>
                  <td className="px-6 py-4 text-center">11%</td>
                  <td className="px-6 py-4 text-center">26%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 projected</td>
                  <td className="px-6 py-4 text-center">£4,482,000</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£5,860,000</td>
                  <td className="px-6 py-4 text-center">£1,761,000</td>
                  <td className="px-6 py-4 text-center">10%</td>
                  <td className="px-6 py-4 text-center">23%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Above DfT allocation</h3>
              <p className="text-emerald-800 text-sm">
                Hull adds internal capital funding on top of DfT allocations. Even in 2023/24, when capital spend
                dipped to £4.0m, it still exceeded the £2.81m DfT figure. The 2025/26 projection of £5.86m against
                £4.482m allocated supports the GREEN Spend scorecard.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The preventative gap</h3>
              <p className="text-amber-800 text-sm">
                Estimated preventative maintenance has ranged from 5% to 11% of spend — never more than one-ninth
                of the budget. Surface dressing and micro-surfacing together treat nearly 2% of the network
                annually (~90,000m²), accounting for roughly 20% of the capital road maintenance budget. At that
                rate, a full-network preventative cycle would take decades.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reactive repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            29,844 defects in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a compact 731km network — an average of 5,968 repairs annually
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Financial year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Roads</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Footways</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,995</td>
                  <td className="px-6 py-4 text-center">1,957</td>
                  <td className="px-6 py-4 text-center font-bold">4,952</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,106</td>
                  <td className="px-6 py-4 text-center">2,581</td>
                  <td className="px-6 py-4 text-center font-bold">5,687</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,997</td>
                  <td className="px-6 py-4 text-center">3,295</td>
                  <td className="px-6 py-4 text-center font-bold">6,292</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,856</td>
                  <td className="px-6 py-4 text-center">3,030</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,886</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,421</td>
                  <td className="px-6 py-4 text-center">2,606</td>
                  <td className="px-6 py-4 text-center font-bold">6,027</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">16,375</td>
                  <td className="px-6 py-4 text-center font-bold">13,469</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">29,844</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~16 repairs a day on roads alone</h3>
              <p className="text-surface-600 text-sm">
                Hull's council forecasts 3,275 road repairs and 2,694 footway repairs annually for 2025/26,
                consistent with the five-year average of 5,968 total defects. On a 731km network, that is a
                steady flow of potholes forming between inspections.
              </p>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">In-house reactive capacity</h3>
              <p className="text-surface-600 text-sm">
                Most reactive maintenance is undertaken internally by Streetscene services, with thin-veneer
                patching used for pre-surface-dressing repairs and shallow residential defects. Peak year was
                2023/24 with 6,886 total repairs — 3,856 on roads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Concrete base admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Concrete bases and climate vulnerability
          </h2>
          <p className="text-surface-600 mb-8">
            Hull's own engineering assessment of why its roads deteriorate — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Specifying the SAMI and PMB surfacing materials is of specific importance to maintenance on the Hull City network, which consists mainly of concrete base constructions, which are more vulnerable to the changes in temperature resulting from climate change."
              source="Hull City Council Local Highways Maintenance Transparency Report 2025/26"
            />
            <QuoteCard
              quote="By effectively utilising preventative maintenance treatments like surface dressing on those sections of road that currently sit within the amber category, the highway asset management team have managed to prevent some of these amber sections from becoming red and keep the percentage of the network that sits within the red category at a steady state over the last 5 years."
              source="Hull City Council Local Highways Maintenance Transparency Report 2025/26"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the concrete admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hull documents that most of its road network sits on concrete bases — a construction type the
                council itself says is more vulnerable to temperature-driven cracking. It has registered extensive
                special engineering difficulty (SED) roads on the National Street Gazetteer because of this.
              </p>
              <p className="text-surface-600 text-sm">
                Documented structural vulnerability is relevant when assessing whether inspection intervals were
                reasonable for your specific road.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The steady-state caveat</h3>
              <p className="text-amber-800 text-sm">
                The council's steady-state claim applies to classified A and B/C roads and surface-dressing
                programmes — not to U1 strategic routes where RED doubled in 2024, nor U2 residential roads
                at 5.4% RED. Headline condition stability masks deterioration on the road types most drivers use
                daily.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN condition, AMBER overall council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hull is better funded than many authorities — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — A and B/C RED held at 1–2%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital exceeds DfT allocation in most years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based safety inspections with nine highway inspectors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management aligned with Well-Managed Highway Infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Confirm Asset Management and permit-scheme coordination
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on well-surveyed A roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U1 strategic roads RED more than doubled to 4.1% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U2 residential RED at 5.4% — highest of any published category
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~507km of residential roads surveyed by walked DVI only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 29,844 pothole repairs in five years — defects form between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative spend at 9–11% — reactive patching dominates
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Concrete-base vulnerability acknowledged by the council
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is road-class specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and spend scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road type</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (council online form, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Whether your road is U1 (SCANNER) or U2 (walked DVI) — different survey precision, different arguments</li>
              <li>• Proximity to concrete-base SED roads — the council's own vulnerability assessment</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Hull's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Hull first
          </h2>
          <p className="text-surface-600 mb-6">
            Hull's transparency report covers reactive maintenance undertaken in line with risk-based safety
            inspections. Reporting the defect through the council creates a dated record — useful evidence if
            the pothole was reported before your incident, or if the council failed to inspect within five working
            days or repair within a reasonable period.
          </p>
          <a
            href="https://www.hull.gov.uk/roads-pavements/report-road-pavement-defects"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Hull City Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Defects on the A63, Castle Street, Roger Millward Way and Hedon Road are National Highways
            responsibility — not Hull. Keep your service request number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Hull?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-rated council still needs a precise claim on the right road type. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U1/U2 survey-method argument</li>
                <li>• No prior-report search</li>
                <li>• No concrete-base vulnerability cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U1 RED doubling to 4.1% documented</li>
                <li>✅ U2 walked-survey gap argued</li>
                <li>✅ 29,844 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Hull</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hull"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Hull claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hull has a GREEN condition scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Condition scorecard is GREEN because SCANNER-surveyed A and B/C roads have kept RED
                sections at a steady 1–2% over five years. That does not cover the whole story: strategic U1
                unclassified roads jumped from 1.6% RED in 2023 to 4.1% in 2024, and residential U2 roads rose
                to 5.4% RED. Section 58 turns on whether your specific defect was reasonably inspected and
                repaired — not on a headline scorecard.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street in Hull?
              </h3>
              <p className="text-surface-600 text-sm">
                Residential U2 roads make up the bulk of Hull's 607km unclassified network. They are surveyed by
                detailed visual inspection — a walked survey by a trained surveyor — not SCANNER laser surveys.
                RED condition on U2 roads reached 5.4% in 2024, up from 4.13% in 2023. Your claim should focus
                on inspection frequency, prior reports, and whether the walked survey would have caught a defect
                of your pothole's size and age.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the U1 bus-route deterioration help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, if your incident was on a U1 strategic road or major bus route. Hull extended SCANNER
                surveys to 100km of U1 roads in 2020, and RED condition on those routes more than doubled from
                1.6% to 4.1% between 2023 and 2024 — while A and B/C roads stayed broadly stable. That is
                documented deterioration on high-traffic corridors the council itself classifies as comparable to
                principal roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hull spends above its DfT allocation — does that block Section 58 arguments?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. Hull's projected 2025/26 capital spend of £5.86m exceeds its £4.482m DfT
                allocation, earning a GREEN Spend scorecard. But estimated preventative maintenance is only 9–11%
                of spend in recent years, and the council still repaired 29,844 potholes and defects across five
                years. High reactive volume suggests defects routinely form between inspections — the scenario
                where prior reports and photographic evidence matter most.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hull uses concrete road bases — does that matter for claims?
              </h3>
              <p className="text-surface-600 text-sm">
                The council states its network consists mainly of concrete base constructions, which are more
                vulnerable to temperature changes from climate change. It specifies premium SAMI and PMB surfacing
                materials partly for that reason. Where a pothole forms on a concrete-base road, the council's
                own engineering assessment acknowledges elevated deterioration risk — relevant when arguing whether
                inspection intervals were reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How quickly does Hull inspect reported potholes?
              </h3>
              <p className="text-surface-600 text-sm">
                Hull's report-a-defect page states inspections within five working days, with repairs scheduled
                within up to 28 days if considered necessary. The transparency report notes nine highway inspectors
                undertaking risk-based safety inspections across the network at various intervals annually. If you
                reported a defect before your incident and it was not repaired in time, that dated record
                strengthens a Section 41 argument.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who maintains the A63 and Castle Street in Hull?
              </h3>
              <p className="text-surface-600 text-sm">
                The A63 Castle Street, Roger Millward Way, Hedon Road and related trunk roads are maintained by
                National Highways — not Hull City Council. The council's transparency report explicitly excludes
                the A63 from its adopted highway network. Claims for potholes on those routes must be directed to
                National Highways, not the city council.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.hull.gov.uk/roads-pavements/highways-planned-maintenance-work"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hull City Council Local Highways Maintenance Transparency Report (2025/26)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
